/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { fetchGraphQl, config } from '@/pdp/api';
import { ProductModel } from '@/pdp/data/models';
import { transformProductData } from '@/pdp/data/transforms';
import { REFINE_PRODUCT_QUERY } from './graphql/RefineProductQuery.graphql';

async function getProductData(sku: string, optionUIDs: string[]): Promise<any> {
  const optionPairCombinations = getPairCombination(optionUIDs);
  const optionQueries = getProductOptionQuery(optionPairCombinations, sku);

  const mergedQueries = REFINE_PRODUCT_QUERY.replace(
    '# %extendedPlaceholder%',
    optionQueries
  );

  const { data } = await fetchGraphQl(mergedQueries, {
    method: 'GET',
    variables: { optionIds: optionUIDs, sku },
  });
  return data;
}

export const getRefinedProduct = async (
  sku: string,
  optionUIDs: string[],
  anchorOptions?: string[],
  raw?: boolean
): Promise<ProductModel | null> => {
  const data = await getProductData(sku, optionUIDs);
  if (!data) return null;

  let { products, refineProduct, ...refinedOptions } = data;

  const parent = products?.[0];

  const options = mergeRefinedOptions(
    Object.values(refinedOptions),
    parent.options,
    anchorOptions
  );

  // needed when anchor options exist
  if (anchorOptions?.length && refineProduct === null) {
    optionUIDs = updateSelections(options, optionUIDs, anchorOptions);
    const updatedRefinedData = await getProductData(sku, optionUIDs);
    refineProduct = updatedRefinedData?.refineProduct;
  }

  const response = {
    ...(refineProduct || parent),
    sku: parent.sku,
    name: parent.name,
    externalParentId: parent?.externalId,
    options,
    optionUIDs,
    variantSku:
      refineProduct?.__typename === 'SimpleProductView'
        ? refineProduct?.sku
        : undefined,
  };

  const refinedData = raw ? response : transformProductData(response);

  const fallback = config?.getConfig()?.models?.ProductDetails?.fallbackData;

  if (fallback) {
    return fallback(parent, refinedData!);
  }

  return refinedData;
};

function getPairCombination(arr: string[]): string[][] {
  if (arr.length < 2) {
    return [arr];
  }

  const result: string[][] = [];

  arr.forEach((item) => {
    const temp: string[] = [];

    arr.forEach((item2) => {
      if (item !== item2) {
        temp.push(item2);
      }
    });

    result.push(temp);
  });

  return result;
}

function getProductOptionQuery(optionUIDs: string[][], sku: string): string {
  return optionUIDs
    .map((ids: string[], i: number) => {
      return `
          ProductOption${i}: refineProduct(
            optionIds: [
              ${ids.map((id) => `"${id}"`).join(', ')}
            ]
            sku: "${sku}"
          ) {
            ... on ComplexProductView {
              options {
                ...PRODUCT_OPTION_FRAGMENT
              }
            }
          }
        `;
    })
    .join('');
}

function mergeRefinedOptions(
  options: {},
  parent: [] = [],
  anchorOptions?: string[]
) {
  // flat _refined[].options[] to _refinedOptions[]
  const refinedOptions: any = Object.values(options)
    .filter((x) => !!x)
    .reduce((acc: any, curr: any) => {
      if (!curr.options) return [...acc];

      return [...acc, ...curr.options];
    }, []);

  // Create a map to store items by their id from original
  const map = new Map(parent.map((item: any) => [item.id, item]));

  // Iterate through refinedOptions and merge items
  refinedOptions.forEach((current: any) => {
    // anchor options should display all values, so no merge is performed
    if (anchorOptions?.includes(current.id)) {
      return;
    }

    map.set(current.id, current);
  });

  return [...map.values()];
}

function updateSelections(
  options: any[],
  optionsUIDs: string[],
  anchorOptions: string[]
): string[] {
  const updatedOptionsUIDs: string[] = [];

  let optionValue: string;
  options.forEach((option: any) => {
    if (anchorOptions.includes(option.id)) {
      optionValue =
        option.values?.find((value: any) => optionsUIDs.includes(value?.id))
          ?.id || option.values[0]?.id;
    } else {
      optionValue = option.values[0]?.id; // TODO check if we should use default value instead
    }
    updatedOptionsUIDs.push(optionValue);
  });
  return updatedOptionsUIDs;
}
